import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UGCLayout from '@/components/layouts/UGCLayout';
import NavigationBar from '@/components/ui/NavigationBar';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasFeatureAccess } from '@/config/featureAccess';
import { ugcDealsService } from '@/services/ugcDealsService';
import { ugcBrandsService } from '@/services/ugcBrandsService';
import { NovaChatProvider } from '@/contexts/NovaChatContext';
import NovaChat from '@/components/NovaChat';

const stageLabels = {
  inbound: 'Inbound',
  negotiating: 'Negotiating',
  delivering: 'Delivering',
  paid: 'Paid'
};

const UGCDashboard = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { ugcTier } = useSubscription();
  const userTier = ugcTier || profile?.ugc_tier || 'free';
  const canAccess = hasFeatureAccess('app/ugc-dashboard', userTier);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ brand: '', value: 0, deliverables: '', stage: 'inbound', due_date: '' });
  const [streak, setStreak] = useState(0);
  const [lastTaskDate, setLastTaskDate] = useState(null);
  const [brands, setBrands] = useState([]);
  const [brandForm, setBrandForm] = useState({ name: '', category: 'CPG', website: '', email: '', notes: '', tags: '' });
  const [brandFilter, setBrandFilter] = useState('all');
  const [brandSearch, setBrandSearch] = useState('');
  const [editingBrandId, setEditingBrandId] = useState(null);
  const [brandMessage, setBrandMessage] = useState('');
  const [loadError, setLoadError] = useState(null);
  const tasks = [
    { id: 'record', label: 'Record or upload a video today', points: 10 },
    { id: 'post', label: 'Post to TikTok/IG/FB/YT (coming soon)', points: 10 },
    { id: 'log', label: 'Log a new brand deal', points: 5 },
  ];
  const ugcNavItems = [
    { path: '/app/ugc-dashboard', label: 'Dashboard', icon: 'Layout' },
    { path: '/app/ugc-marketplace', label: 'Marketplace', icon: 'ShoppingBag' },
    { path: '/app/ugc-invoices', label: 'Invoices', icon: 'DollarSign' },
    { path: '/app/ugc-brands', label: 'Brands', icon: 'Database' },
    { path: '/app/ugc-scripts', label: 'Scripts', icon: 'FileText' },
    { path: '/app/teleprompter', label: 'Teleprompter', icon: 'PlayCircle' },
  ];

  const ugcMoreItems = [
    { path: '/app/ugc-hub', label: 'Creator Hub', icon: 'Compass' },
    { path: '/app/ugc-crm', label: 'CRM', icon: 'Users' },
    { path: '/app/ugc-community', label: 'Community', icon: 'MessageCircle' },
    { path: '/app/ugc-media', label: 'Media Library', icon: 'Film' },
  ];

  useEffect(() => {
    if (!profile?.id) return;
    const load = async () => {
      setLoading(true);
      try {
        const data = await ugcDealsService.list(profile.id);
        setDeals(Array.isArray(data) ? data : []);
        setLoadError(null);
      } catch (err) {
        console.error('[UGC Dashboard] Failed to load deals:', err);
        setLoadError('We could not load your UGC data. Please try again or refresh.');
      } finally {
        setLoading(false);
      }
    };
    load();
    const storedStreak = Number(localStorage.getItem('ugc_streak') || 0);
    const storedDate = localStorage.getItem('ugc_last_task_date');
    setStreak(storedStreak);
    setLastTaskDate(storedDate);

    const loadBrands = async () => {
      try {
        const list = await ugcBrandsService.list();
        setBrands(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('[UGC Dashboard] Failed to load brands:', err);
        // Don't set loadError for brands; dashboard should still render
      }
    };
    loadBrands();
  }, [profile?.id]);

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 text-muted-foreground">
        Loading your UGC workspace...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-muted/30 text-center px-4">
        <Icon name="AlertTriangle" size={32} className="text-amber-600 mb-2" />
        <p className="text-foreground font-semibold mb-2">Something went wrong loading UGC</p>
        <p className="text-sm text-muted-foreground mb-4">{loadError}</p>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!profile?.id || !form.brand) return;
    setLoading(true);
    try {
      const created = await ugcDealsService.create(profile.id, form);
      setDeals([created, ...deals]);
      setForm({ brand: '', value: 0, deliverables: '', stage: 'inbound', due_date: '' });
      markTaskComplete('log');
    } catch (err) {
      console.error('Failed to create deal', err);
    } finally {
      setLoading(false);
    }
  };

  const markTaskComplete = (taskId) => {
    const today = new Date().toISOString().slice(0, 10);
    if (lastTaskDate !== today) {
      const newStreak = lastTaskDate ? streak + 1 : 1;
      setStreak(newStreak);
      localStorage.setItem('ugc_streak', String(newStreak));
      localStorage.setItem('ugc_last_task_date', today);
      setLastTaskDate(today);
    }
  };

  const handleAddBrand = (e) => {
    e.preventDefault();
    if (!brandForm.name.trim()) return;
    const tags = brandForm.tags
      ? brandForm.tags.split(',').map((t) => t.trim()).filter(Boolean)
      : [];
    const payload = {
      name: brandForm.name.trim(),
      category: brandForm.category,
      website: brandForm.website?.trim(),
      email: brandForm.email?.trim(),
      notes: brandForm.notes?.trim(),
      tags
    };
    const action = editingBrandId
      ? ugcBrandsService.update(editingBrandId, payload)
      : ugcBrandsService.create(profile?.id, payload);

    action.then((result) => {
      if (editingBrandId) {
        setBrands(brands.map((b) => (b.id === editingBrandId ? result : b)));
      } else {
        setBrands([result, ...brands]);
      }
      setBrandForm({ name: '', category: 'CPG', website: '', email: '', notes: '', tags: '' });
      setEditingBrandId(null);
    }).catch((err) => {
      console.error('Failed to save brand', err);
    });
  };

  const handleEditBrand = (brand) => {
    setEditingBrandId(brand.id);
    setBrandForm({
      name: brand.name || '',
      category: brand.category || 'CPG',
      website: brand.website || '',
      email: brand.email || '',
      notes: brand.notes || '',
      tags: Array.isArray(brand.tags) ? brand.tags.join(', ') : ''
    });
  };

  const handleDeleteBrand = (id) => {
    if (!window.confirm('Delete this brand from your list?')) return;
    ugcBrandsService.remove(id).then(() => {
      setBrands(brands.filter((b) => b.id !== id));
      if (editingBrandId === id) {
        setEditingBrandId(null);
        setBrandForm({ name: '', category: 'CPG', website: '', email: '', notes: '', tags: '' });
      }
    }).catch((err) => {
      console.error('Failed to delete brand', err);
    });
  };

  const allBrands = useMemo(() => {
    return brands
      .filter((brand) => {
        const matchesCategory = brandFilter === 'all' || brand.category === brandFilter;
        const term = brandSearch.trim().toLowerCase();
        const matchesSearch = !term
          || brand.name.toLowerCase().includes(term)
          || (brand.notes || '').toLowerCase().includes(term)
          || (brand.tags || []).some((t) => t.toLowerCase().includes(term));
        return matchesCategory && matchesSearch;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [brandFilter, brandSearch, brands]);

  const stats = useMemo(() => {
    const monthDeals = deals.length;
    const revenue = deals.reduce((sum, d) => sum + (d.stage === 'paid' || d.stage === 'delivering' ? Number(d.value || 0) : 0), 0);
    const pipeline = deals.filter(d => d.stage !== 'paid').reduce((sum, d) => sum + Number(d.value || 0), 0);
    return { monthDeals, revenue, pipeline };
  }, [deals]);

  const renderKanban = () => {
    const stages = ['inbound', 'negotiating', 'delivering', 'paid'];
    const colors = {
      inbound: 'white/5',
      negotiating: 'white/5',
      delivering: 'white/5',
      paid: 'white/5',
    };
    const icons = {
      inbound: 'Inbox',
      negotiating: 'MessageSquare',
      delivering: 'Truck',
      paid: 'CheckCircle',
    };
    return (
      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Icon name="Kanban" size={16} /> Deals Kanban
          </h3>
          <p className="text-xs text-amber-100/60">Drag & drop coming soon</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {stages.map((stage) => {
            const stageDeals = deals.filter((d) => d.stage === stage);
            return (
              <div
                key={stage}
                className={`rounded-xl border border-white/10 bg-${colors[stage]} p-3 flex flex-col gap-2 min-h-[200px] shadow-sm`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon name={icons[stage]} size={16} />
                    <span className="text-sm font-semibold text-white">{stageLabels[stage]}</span>
                  </div>
                  <span className="text-xs text-amber-100/60">{stageDeals.length}</span>
                </div>
                <div className="space-y-2">
                  {stageDeals.length === 0 && (
                    <p className="text-xs text-amber-100/60">No deals yet</p>
                  )}
                  {stageDeals.map((deal) => (
                    <div key={deal.id} className="rounded-lg bg-white/5 border border-white/10 p-3 shadow-xs">
                      <p className="text-sm font-semibold text-white">{deal.brand}</p>
                      <p className="text-xs text-amber-100/60">${Number(deal.value || 0).toLocaleString()}</p>
                      {deal.deliverables && (
                        <p className="text-[11px] text-amber-100/60 mt-1 line-clamp-2">{deal.deliverables}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPreview = () => (
    <div className="mb-10 overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-lg">
      <div className="p-8 md:p-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold rounded-full bg-amber-500/20 text-amber-100 border border-amber-500/30">
            <Icon name="Crown" size={14} />
            Elite / Creator
          </span>
          <span className="text-xs text-amber-100">UGC Dashboard</span>
        </div>
        <h2 className="text-3xl font-bold text-white mb-3">
          Track deals, revenue, and content delivery
        </h2>
        <p className="text-amber-100 mb-6 max-w-3xl">
          A creator-first dashboard: deal tracker, leaderboards, teleprompter access, and quick links to scripts, outreach, and invoices.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[
            { icon: 'Layout', title: 'Deal lanes', desc: 'Inbound, Negotiating, Delivering, Paid with revenue rollups.' },
            { icon: 'Trophy', title: 'Leaderboards', desc: 'Top deals and revenue this month across creators.' },
            { icon: 'Video', title: 'UGC tools', desc: 'Scripts, shot lists, outreach, invoices, teleprompter.' }
          ].map((item) => (
            <div key={item.title} className="rounded-xl border border-white/10 bg-white/5 p-4 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-100 flex items-center justify-center mb-3">
                <Icon name={item.icon} size={18} />
              </div>
              <h3 className="text-sm font-semibold text-white mb-1">{item.title}</h3>
              <p className="text-sm text-amber-100/80">{item.desc}</p>
            </div>
          ))}
        </div>
        <Button
          onClick={() => (window.location.href = '/app/subscription')}
          className="bg-amber-500 hover:bg-amber-600 text-white"
        >
          Upgrade to access
        </Button>
      </div>
    </div>
  );

  const renderStats = () => (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {[ 
        { label: 'Deals this month', value: stats.monthDeals, icon: 'Handshake' },
        { label: 'Revenue (delivering/paid)', value: `$${stats.revenue.toLocaleString()}`, icon: 'DollarSign' },
        { label: 'Pipeline', value: `$${stats.pipeline.toLocaleString()}`, icon: 'BarChart' },
        { label: 'Streak', value: `${streak} days`, icon: 'Flame' }
      ].map((card) => (
        <div key={card.label} className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm flex items-center gap-3"> 
          <div className="w-11 h-11 rounded-xl bg-amber-500/20 flex items-center justify-center shadow-xs">
            <Icon name={card.icon} size={20} className="text-amber-100" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-amber-100/60">{card.label}</p>
            <p className="text-2xl font-bold text-white">{card.value}</p>
          </div>
        </div>
      ))}
    </div>
  );

  const renderLanes = () => {
    const stages = ['inbound', 'negotiating', 'delivering', 'paid'];
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {stages.map((stage) => {
          const stageDeals = deals.filter(d => d.stage === stage);
          return (
            <div key={stage} className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Icon name={stage === 'paid' ? 'CheckCircle' : stage === 'delivering' ? 'Play' : stage === 'negotiating' ? 'MessageSquare' : 'Inbox'} size={16} />
                  {stageLabels[stage]}
                </h3>
                <span className="text-xs text-amber-100/60">{stageDeals.length}</span>
              </div>
              <div className="space-y-3">
                {stageDeals.map((deal) => (
                  <div key={deal.id} className="p-3 rounded-lg border border-white/10 bg-white/5">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-white">{deal.brand}</p>
                      <p className="text-sm text-amber-100 font-semibold">${Number(deal.value || 0)}</p>
                    </div>
                    <p className="text-xs text-amber-100/60">{deal.deliverables}</p>
                    <p className="text-xs text-amber-100/60">{deal.due_date ? `Due ${deal.due_date}` : 'No due date'}</p>
                  </div>
                ))}
                {stageDeals.length === 0 && (
                  <p className="text-xs text-amber-100/60">No deals here yet.</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderLeaderboards = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Icon name="Trophy" size={16} /> Top deals (sample)
          </h3>
        </div>
        <ul className="space-y-2">
          {deals
            .slice()
            .sort((a, b) => Number(b.value || 0) - Number(a.value || 0))
            .slice(0, 5)
            .map((deal) => (
              <li key={deal.id} className="p-3 rounded-lg bg-white/5 border border-white/10 text-sm text-amber-100">
                ${Number(deal.value || 0)} - {deal.brand}
              </li>
            ))}
        </ul>
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Icon name="Users" size={16} /> Community cues
          </h3>
        </div>
        <ul className="space-y-2 text-sm text-amber-100">
          <li className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">Ask for feedback on your hook: "Stop wasting money on…"</li>
          <li className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">Share analytics screenshots to get CTA tweaks.</li>
          <li className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">Swap briefs with other creators; pair feedback with teleprompter.</li>
        </ul>
      </div>
    </div>
  );

  const renderCreatorHub = () => (
    <div id="community" className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Icon name="Compass" size={16} /> Creator Hub • What's hot
        </h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="p-3 rounded-lg bg-white/5 border border-white/10">
          <p className="text-xs uppercase text-amber-100/60 mb-1">Trending niches</p>
          <ul className="text-sm text-amber-100 space-y-1">
            <li>• Hair care “glass hair” routines</li>
            <li>• Supplements “sleep/relax”</li>
            <li>• Fitness “glute/leg day hacks”</li>
          </ul>
        </div>
        <div className="p-3 rounded-lg bg-white/5 border border-white/10">
          <p className="text-xs uppercase text-amber-100/60 mb-1">Platform notes</p>
          <ul className="text-sm text-amber-100 space-y-1">
            <li>• TikTok: 9–15s; text overlays; trending audio low</li>
            <li>• Reels: 15–30s; safe zones; hook in 1s</li>
            <li>• Shorts: 30–45s; VO clarity; end-card CTA</li>
          </ul>
        </div>
        <div className="p-3 rounded-lg bg-white/5 border border-white/10">
          <p className="text-xs uppercase text-amber-100/60 mb-1">Hooks to test</p>
          <ul className="text-sm text-amber-100 space-y-1">
            <li>• “POV: You need this before your next scroll ends”</li>
            <li>• “Creators: I used this to boost engagement +27%”</li>
            <li>• “Stop wasting money on dupes—try this instead”</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderQuickLinks = () => (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
        <Icon name="Zap" size={16} /> Quick links
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { label: 'Marketplace', path: '/app/ugc-marketplace', icon: 'ShoppingBag' },
          { label: 'UGC Scripts', path: '/app/ugc-scripts', icon: 'Video' },
          { label: 'Teleprompter', path: '/app/teleprompter', icon: 'Music' },
          { label: 'Invoices/Packages', path: '/app/ugc-scripts#invoice', icon: 'CreditCard' },
          { label: 'Outreach', path: '/app/ugc-scripts#outreach', icon: 'Send' },
        ].map((link) => (
          <button
            key={link.label}
            onClick={() => (window.location.href = link.path)}
            className="p-3 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition text-left"
          >
            <div className="flex items-center gap-2">
              <Icon name={link.icon} size={16} />
              <span className="text-sm font-medium text-white">{link.label}</span>
            </div>
          </button>
        ))}
      </div>
      <div className="mt-4">
        <h4 className="text-xs uppercase text-muted-foreground mb-2">Add deal</h4>
        <form className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2" onSubmit={handleCreate}>
          <input
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
            placeholder="Brand"
            value={form.brand}
            onChange={(e) => setForm({ ...form, brand: e.target.value })}
            required
          />
          <input
            type="number"
            min="0"
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
            placeholder="Value"
            value={form.value}
            onChange={(e) => setForm({ ...form, value: Number(e.target.value) })}
          />
          <select
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
            value={form.stage}
            onChange={(e) => setForm({ ...form, stage: e.target.value })}
          >
            <option value="inbound">Inbound</option>
            <option value="negotiating">Negotiating</option>
            <option value="delivering">Delivering</option>
            <option value="paid">Paid</option>
          </select>
          <input
            type="date"
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
            value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
          />
          <input
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm sm:col-span-2 md:col-span-4"
            placeholder="Deliverables (optional)"
            value={form.deliverables}
            onChange={(e) => setForm({ ...form, deliverables: e.target.value })}
          />
          <div className="sm:col-span-2 md:col-span-4">
            <Button type="submit" size="sm" disabled={loading}>
              {loading ? 'Saving...' : 'Save deal'}
            </Button>
          </div>
        </form>
      </div>
      <div className="mt-6">
        <h4 className="text-xs uppercase text-muted-foreground mb-2">Daily tasks</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {tasks.map((task) => (
            <div key={task.id} className="p-3 rounded-lg border border-border/60 bg-muted/30 flex items-center justify-between">
              <div>
                <p className="text-sm text-foreground">{task.label}</p>
                <p className="text-xs text-muted-foreground">{task.points} pts</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => markTaskComplete(task.id)}>
                Mark done
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBrandDatabase = () => {
    const categories = ['all', 'Beauty', 'CPG', 'Fitness', 'Apparel', 'Home', 'Tech', 'Apps'];
    return (
      <div id="brands" className="rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="Database" size={16} /> Brand database
          </h3>
          <div className="flex items-center gap-2">
            <select
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'All categories' : cat}
                </option>
              ))}
            </select>
            <input
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
              placeholder="Search brands, tags, notes"
              value={brandSearch}
              onChange={(e) => setBrandSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {allBrands.map((brand) => {
            const canEdit = !!profile?.id && brand.created_by === profile.id;
            return (
              <div key={brand.id} className="rounded-xl border border-border bg-muted/30 p-3 flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-base font-semibold text-foreground">{brand.name}</p>
                  <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
                    {brand.category}
                  </span>
                </div>
                {brand.website && (
                  <a
                    href={brand.website}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-primary hover:underline break-all"
                  >
                    {brand.website}
                  </a>
                )}
                {brand.email && (
                  <a
                    href={`mailto:${brand.email}`}
                    className="text-xs text-foreground hover:underline break-all"
                  >
                    {brand.email}
                  </a>
                )}
                {brand.email && (
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(brand.email);
                        setBrandMessage('Email copied');
                        setTimeout(() => setBrandMessage(''), 1500);
                      } catch {
                        setBrandMessage('Copy not available');
                        setTimeout(() => setBrandMessage(''), 1500);
                      }
                    }}
                  >
                    Copy email
                  </Button>
                )}
                {brand.notes && <p className="text-sm text-muted-foreground">{brand.notes}</p>}
                {brand.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {brand.tags.map((tag) => (
                      <span key={tag} className="text-[11px] px-2 py-1 rounded-full bg-background border border-border text-muted-foreground">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                {canEdit && (
                  <div className="flex items-center gap-2 pt-2">
                    <Button size="xs" variant="outline" onClick={() => handleEditBrand(brand)}>
                      Edit
                    </Button>
                    <Button size="xs" variant="ghost" className="text-destructive" onClick={() => handleDeleteBrand(brand.id)}>
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {brandMessage && <p className="text-xs text-primary mt-2">{brandMessage}</p>}

        <div className="mt-5 border-t border-border pt-4">
          <h4 className="text-xs uppercase text-muted-foreground mb-2">Add your own brands</h4>
          <form className="grid grid-cols-1 md:grid-cols-2 gap-3" onSubmit={handleAddBrand}>
            <input
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
              placeholder={editingBrandId ? 'Brand name (editing)' : 'Brand name'}
              value={brandForm.name}
              onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })}
              required
            />
            <select
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={brandForm.category}
              onChange={(e) => setBrandForm({ ...brandForm, category: e.target.value })}
            >
              {categories.filter((c) => c !== 'all').map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <input
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
              placeholder="Website (optional)"
              value={brandForm.website}
              onChange={(e) => setBrandForm({ ...brandForm, website: e.target.value })}
            />
            <input
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
              placeholder="Email (for outreach)"
              value={brandForm.email}
              onChange={(e) => setBrandForm({ ...brandForm, email: e.target.value })}
              required
            />
            <input
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
              placeholder="Tags (comma separated, e.g., skincare, makeup)"
              value={brandForm.tags}
              onChange={(e) => setBrandForm({ ...brandForm, tags: e.target.value })}
            />
            <textarea
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm md:col-span-2"
              placeholder="Notes (hook ideas, offers, what performs well)"
              value={brandForm.notes}
              onChange={(e) => setBrandForm({ ...brandForm, notes: e.target.value })}
            />
            <div className="md:col-span-2">
              <div className="flex items-center gap-2">
                <Button type="submit" size="sm">
                  {editingBrandId ? 'Update brand' : 'Save brand'}
                </Button>
                {editingBrandId && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingBrandId(null);
                      setBrandForm({ name: '', category: 'CPG', website: '', email: '', notes: '', tags: '' });
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <UGCLayout>
      <NavigationBar items={ugcNavItems} moreItems={ugcMoreItems} hideLocks homePath="/app/ugc-dashboard" />
      <main className="max-w-7xl mx-auto px-4 py-10 mb-20">
        <div className="mb-10 overflow-hidden rounded-3xl border border-white/10 bg-white/5 shadow-lg">
          <div className="p-6 md:p-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-100 text-xs font-semibold border border-amber-500/30 shadow-sm">
                <Icon name="Layout" size={14} />
                UGC Deal Room
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight">Run your UGC pipeline like a pro</h1>
              <p className="text-amber-100 max-w-2xl">
                Track deals, revenue, and delivery—plus one-click access to scripts, outreach, teleprompter, and brand intelligence.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => navigate('/app/ugc-hub')}>
                  <Icon name="Compass" size={14} className="mr-1" /> Creator Hub
                </Button>
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => navigate('/app/ugc-marketplace')}>
                  <Icon name="ShoppingBag" size={14} className="mr-1" /> Marketplace
                </Button>
                <Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white border-transparent" onClick={() => navigate('/app/ugc-brands')}>
                  <Icon name="Database" size={14} className="mr-1" /> Brand DB
                </Button>
                <Button size="sm" className="bg-indigo-500 hover:bg-indigo-600 text-white border-transparent" onClick={() => navigate('/app/ugc-scripts')}>
                  <Icon name="FileText" size={14} className="mr-1" /> Scripts
                </Button>
                <Button size="sm" className="bg-purple-500 hover:bg-purple-600 text-white border-transparent" onClick={() => navigate('/app/teleprompter')}>
                  <Icon name="PlayCircle" size={14} className="mr-1" /> Teleprompter
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 md:w-80">
              {[{label:'Active deals', value: deals.length, icon:'Kanban'},{label:'Paid/Delivering', value: deals.filter(d=>d.stage==='paid'||d.stage==='delivering').length, icon:'CheckCircle'},{label:'Revenue', value: `$${stats.revenue.toLocaleString()}`, icon:'DollarSign'},{label:'Pipeline', value: `$${stats.pipeline.toLocaleString()}`, icon:'BarChart2'}].map((card) => (
                <div key={card.label} className="rounded-xl border border-white/10 bg-white/5 p-3 shadow-sm">
                  <div className="flex items-center gap-2 text-amber-100 text-xs font-semibold">
                    <Icon name={card.icon} size={14} />
                    {card.label}
                  </div>
                  <p className="text-lg font-bold text-white mt-1">{card.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {!canAccess && renderPreview()}
        {canAccess && (
          <div className="space-y-8">
            {renderStats()}
            {renderKanban()}
            {renderLanes()}
            {renderLeaderboards()}
          </div>
        )}
      </main>
      <NovaChat />
    </UGCLayout>
  );
};

const UGCDashboardWithProvider = () => (
  <NovaChatProvider>
    <UGCDashboard />
  </NovaChatProvider>
);

export default UGCDashboardWithProvider;
